import * as React from "react";
import { Text } from "components/elementsThemed/Text";
import { Input } from "components/elementsThemed/Field";
import { useTemplate } from "hooks/template";
import { Copy } from "utils";
import { FETCH_ADDRESS } from "utils/api";
import { AddressResult, ListBox, Popover, useAddressSearch } from "./shared";
import { GeolocationButton } from "./GeolocationButton";
import css from "./AddressSearch.module.scss";

interface PickupKioskSearchProps {
  onSelect(id: AddressResult["id"]): void;
  onGeo(coords: { lat: number; long: number }): void;
  orderType: "pickup" | "kiosk";
}

/**
 * Renders input and search suggestions for Pickup and Kiosk
 * orders as a combobox.
 *
 * Behaviors:
 * - When an address is selected, `onSelect` is called with the
 *   places ID. This component is used by the `withLocation` HOC,
 *   which uses this ID to update the map and provide suggestions.
 *   (Pickup and kiosk orders don't use addresses directly the same
 *   way that delivery orders do– it's just a filter for locations close
 *   to the user)
 * - The input renders a <GeolocationButton/> that allows the user
 *   to let the browser determine their location.
 */
const PickupKioskSearch: React.FC<PickupKioskSearchProps> = ({
  onSelect,
  orderType,
  onGeo,
}) => {
  const template = useTemplate("address");
  const {
    comboBoxProps,
    comboBoxState,
    fetching,
    inputRef,
    popoverRef,
    listBoxRef,
  } = useAddressSearch({
    onSelect: (address) => onSelect(address.id),
    orderType,
    fetch: FETCH_ADDRESS,
  });

  return (
    <div className={css["seach-container"]}>
      <div className={css["search-dropdown"]}>
        <label {...comboBoxProps.labelProps}>
          <Text type={template.labels.label}>
            {Copy.RG_INFO_STATIC.LOCATION_TEXT}
          </Text>
        </label>

        <Input
          {...comboBoxProps.inputProps}
          inputRef={inputRef}
          className={[css.input]}
          icon={<GeolocationButton onGeo={onGeo} />}
          placeholder={
            Copy.PICKUP_FORM_STATIC.FORM_SEARCH_FORM_ADDRESS_PLACEHOLDER
          }
          type={template.inputs.standard}
        />

        {fetching && <p>{Copy.LIVE_SEARCH_STATIC.MESSAGE}</p>}

        {comboBoxState.isOpen && (
          <Popover
            popoverRef={popoverRef}
            isOpen={comboBoxState.isOpen}
            onClose={comboBoxState.close}
          >
            <ListBox
              {...comboBoxProps.listBoxProps}
              listBoxRef={listBoxRef}
              state={comboBoxState}
            />
          </Popover>
        )}
      </div>
    </div>
  );
};

export { PickupKioskSearch };
